import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LogOut, ChevronDown, ChevronRight,
  LayoutDashboard, ShieldAlert, Eye, EyeOff,
  PanelLeftClose, PanelLeftOpen, X, Star,
} from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';
import { logout } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import logo from '../../assets/bmp-logo-final-v2.png';
import NAVIGATION_STRUCTURE from '../../config/navigation';
import { useSidebar } from '../../contexts/SidebarContext';
import useFavorites from '../../hooks/useFavorites';

/**
 * INSTITUTIONAL SIDEBAR — v3 (Dynamic Collapsible)
 *
 * - Expanded (260px): icons + labels + sub-menus
 * - Collapsed (70px): icons only + hover tooltips
 * - Persistent state via SidebarContext → localStorage['bmp_sidebar_state']
 * - Mobile: hidden by default, drawer via overlay
 * - Dark theme per spec (#0F172A background)
 */

const STATUS_BADGE = {
  complete: 'bg-emerald-500/20 text-emerald-300',
  in_progress: 'bg-amber-500/20 text-amber-300',
  planned: 'bg-slate-600/40 text-slate-400',
};

const W_EXPANDED = 260;
const W_COLLAPSED = 70;


const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { isCollapsed, toggleCollapsed, isMobileOpen, closeMobile } = useSidebar();

  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const raw = localStorage.getItem('bmp_sidebar_expanded');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* no-op */ }
    return {};
  });

  const [showPlanned, setShowPlanned] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    Object.keys(NAVIGATION_STRUCTURE).forEach((key) => {
      const section = NAVIGATION_STRUCTURE[key];
      if (section.modules.some((m) => path.startsWith(m.path))) {
        setExpandedSections((prev) => ({ ...prev, [key]: true }));
      }
    });
  }, [location.pathname]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('bmp_sidebar_expanded', JSON.stringify(next)); } catch (_) { /* no-op */ }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    toast({ title: t('common.logout'), description: t('auth.logoutSuccess') });
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const isSectionActive = (section) =>
    section.modules.some((m) => location.pathname.startsWith(m.path));

  const handleNavClick = () => {
    if (isMobileOpen) closeMobile();
  };

  const widthClass = isCollapsed ? 'w-[70px]' : 'w-[260px]';
  const baseClasses = `h-screen bg-[#0F172A] text-[#CBD5E1] border-r border-slate-800 flex flex-col overflow-hidden ${widthClass}`;
  const animStyle = { transition: 'width 0.25s ease, transform 0.25s ease' };

  // Mobile: overlay + drawer
  const mobileClasses = `fixed inset-y-0 left-0 z-50 lg:hidden ${
    isMobileOpen ? 'translate-x-0' : '-translate-x-full'
  }`;

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div onClick={closeMobile}
             data-testid="sidebar-mobile-overlay"
             className="fixed inset-0 bg-black/50 z-40 lg:hidden" />
      )}

      {/* Desktop */}
      <aside className={`${baseClasses} hidden lg:flex`}
             style={{ ...animStyle, width: isCollapsed ? W_COLLAPSED : W_EXPANDED }}
             data-testid="admin-sidebar" data-collapsed={isCollapsed}>
        <SidebarBody
          isCollapsed={isCollapsed}
          toggleCollapsed={toggleCollapsed}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          isActive={isActive}
          isSectionActive={isSectionActive}
          showPlanned={showPlanned}
          setShowPlanned={setShowPlanned}
          handleLogout={handleLogout}
          handleNavClick={handleNavClick}
          toast={toast}
          t={t}
          logo={logo}
          onMobileClose={null}
        />
      </aside>

      {/* Mobile drawer */}
      <aside className={`${baseClasses} ${mobileClasses}`}
             style={{ ...animStyle, width: W_EXPANDED }}
             data-testid="admin-sidebar-mobile" data-mobile-open={isMobileOpen}>
        <SidebarBody
          isCollapsed={false}
          toggleCollapsed={toggleCollapsed}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          isActive={isActive}
          isSectionActive={isSectionActive}
          showPlanned={showPlanned}
          setShowPlanned={setShowPlanned}
          handleLogout={handleLogout}
          handleNavClick={handleNavClick}
          toast={toast}
          t={t}
          logo={logo}
          onMobileClose={closeMobile}
        />
      </aside>
    </>
  );
};


// ======================================================================
// Shared body for desktop + mobile
// ======================================================================
const SidebarBody = ({
  isCollapsed, toggleCollapsed, expandedSections, toggleSection,
  isActive, isSectionActive, showPlanned, setShowPlanned,
  handleLogout, handleNavClick, toast, t, logo, onMobileClose,
}) => {
  const { isFavorite, toggle: toggleFav } = useFavorites();
  const myUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('admin_user') || '{}');
    } catch { return {}; }
  })();
  const myRole = myUser?.role || null;
  const myModules = myUser?.modules_access || [];
  // Owner + admin_l2 see everything; others must have section's module code
  // (lowercased section.code prefix matched against modules_access).
  const sectionToModule = {
    P0: "cms", P1: "finance", P2: "crm", P3: "assistant", P4: "products",
    P5: "risk", P6: "procurement", P7: "automation", P8: "onboarding",
  };
  const canSeeSection = (section) => {
    // Defensive: if we don't know the role yet (stale cache, fresh login),
    // do NOT hide. The backend middleware enforces writes anyway.
    if (!myRole) return true;
    if (Array.isArray(section.requiredRoles) && section.requiredRoles.length) {
      return section.requiredRoles.includes(myRole);
    }
    if (myRole === 'owner' || myRole === 'admin_l2') return true;
    if ((myModules || []).includes('*')) return true;
    const moduleCode = sectionToModule[section.code];
    return moduleCode ? (myModules || []).includes(moduleCode) : true;
  };
  return (
  <TooltipProvider delayDuration={120}>
    {/* Logo + toggle */}
    <div className="flex items-center justify-between px-3 py-4 border-b border-slate-800">
      {!isCollapsed ? (
        <img src={logo} alt="Grupo BMP" className="h-9 ml-2" />
      ) : (
        <div className="w-9 h-9 mx-auto rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#F97316] flex items-center justify-center shadow-lg shadow-orange-500/20">
          <span className="text-white font-bold text-sm">BMP</span>
        </div>
      )}
      {onMobileClose ? (
        <button onClick={onMobileClose}
                data-testid="sidebar-mobile-close"
                className="p-2 rounded hover:bg-slate-800 text-slate-300">
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={toggleCollapsed}
                    data-testid="sidebar-collapse-toggle"
                    className={`p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors ${isCollapsed ? 'mx-auto' : ''}`}>
              {isCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700 text-xs">
            {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          </TooltipContent>
        </Tooltip>
      )}
    </div>

    {/* Nav */}
    <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-1 px-2">
      {/* Dashboard */}
      <NavItem
        to="/admin/dashboard"
        label="Dashboard"
        icon={LayoutDashboard}
        isCollapsed={isCollapsed}
        active={isActive('/admin/dashboard')}
        onClick={handleNavClick}
        testId="sidebar-dashboard-link"
      />

      {/* Command Center */}
      <NavItem
        to="/admin/command-center"
        label="Command Center"
        icon={ShieldAlert}
        isCollapsed={isCollapsed}
        active={isActive('/admin/command-center')}
        onClick={handleNavClick}
        testId="sidebar-command-center-link"
        accent
      />

      <div className="my-2 mx-2 border-t border-slate-800" />

      {Object.keys(NAVIGATION_STRUCTURE).map((sectionKey) => {
        const section = NAVIGATION_STRUCTURE[sectionKey];
        // Role + module-access gating
        if (!canSeeSection(section)) return null;
        const SectionIcon = section.icon;
        const isExp = !!expandedSections[sectionKey];
        const hasActive = isSectionActive(section);
        const visibleModules = section.modules
          .filter((m) => m.enabled || showPlanned)
          .filter((m) => !Array.isArray(m.requiredRoles)
            || m.requiredRoles.includes(myRole));

        const headerBase = `w-full flex items-center rounded-md text-sm font-medium transition-colors ${
          hasActive ? 'bg-slate-800 text-white' : 'text-[#CBD5E1] hover:bg-slate-800 hover:text-white'
        }`;

        const headerContent = (
          <button
            onClick={() => toggleSection(sectionKey)}
            data-testid={`sidebar-section-toggle-${section.code}`}
            className={`${headerBase} ${isCollapsed ? 'justify-center px-0 py-2.5' : 'justify-between px-3 py-2'}`}
          >
            <div className="flex items-center min-w-0">
              <SectionIcon className="w-[22px] h-[22px] flex-shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="ml-3 truncate">{section.label}</span>
                  <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-mono font-bold rounded ${STATUS_BADGE[section.status] || STATUS_BADGE.planned}`}>
                    {section.code}
                  </span>
                </>
              )}
            </div>
            {!isCollapsed && (isExp ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-60" /> : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />)}
          </button>
        );

        return (
          <div key={sectionKey} data-testid={`sidebar-section-${section.code}`}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>{headerContent}</TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs">{section.label}</span>
                    <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${STATUS_BADGE[section.status] || STATUS_BADGE.planned}`}>
                      {section.code}
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : headerContent}

            {/* Sub-items only when expanded and section open */}
            {!isCollapsed && isExp && (
              <div className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-800 pl-2">
                <Link
                  to={`/admin/dashboard/modules/${section.code}`}
                  data-testid={`sidebar-module-overview-${section.code}`}
                  onClick={handleNavClick}
                  className={`flex items-center px-2 py-1 rounded text-[11px] italic transition-colors ${
                    isActive(`/admin/dashboard/modules/${section.code}`)
                      ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  → Overview
                </Link>

                {visibleModules.map((module) => {
                  const ModuleIcon = module.icon;
                  const moduleActive = isActive(module.path);
                  const fav = isFavorite(module.id);
                  return (
                    <div key={module.id} className="group/mod relative flex items-center">
                      <Link
                        to={module.path}
                        data-testid={`sidebar-module-${module.id}`}
                        onClick={(e) => {
                          if (!module.enabled) {
                            e.preventDefault();
                            toast({ title: 'Coming Soon', description: `${module.label} is under development.` });
                            return;
                          }
                          handleNavClick();
                        }}
                        className={`flex-1 flex items-center justify-between px-2 py-1.5 rounded text-[12.5px] transition-colors ${
                          moduleActive
                            ? 'bg-[#00B4D8] text-white'
                            : module.enabled
                            ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                            : 'text-slate-500 hover:bg-slate-800/50'
                        }`}
                      >
                        <div className="flex items-center min-w-0">
                          {ModuleIcon && <ModuleIcon className="h-3.5 w-3.5 mr-2 flex-shrink-0 opacity-80" />}
                          <span className="truncate">{module.label}</span>
                        </div>
                        {!module.enabled && (
                          <span className="ml-2 px-1 py-0 text-[8px] bg-amber-500/30 text-amber-300 rounded uppercase font-bold">Soon</span>
                        )}
                      </Link>
                      {module.enabled && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(module.id); }}
                          data-testid={`sidebar-pin-${module.id}`}
                          title={fav ? 'Remove from Quick Access' : 'Pin to Quick Access'}
                          className={`ml-1 p-1 rounded transition-all ${
                            fav
                              ? 'text-[#FF6B35] opacity-100'
                              : 'text-slate-500 opacity-0 group-hover/mod:opacity-100 hover:text-[#FF6B35]'
                          }`}>
                          <Star className={`h-3 w-3 ${fav ? 'fill-current' : ''}`} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>

    {/* Toggle planned */}
    <div className="px-3 py-2 border-t border-slate-800">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setShowPlanned((v) => !v)}
            data-testid="sidebar-toggle-planned"
            className={`w-full flex items-center text-[11px] text-slate-400 hover:text-white ${
              isCollapsed ? 'justify-center' : 'gap-1.5'
            }`}
          >
            {showPlanned ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {!isCollapsed && <span>{showPlanned ? 'Hide Coming Soon' : 'Show Coming Soon'}</span>}
          </button>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700 text-xs">
            {showPlanned ? 'Hide Coming Soon' : 'Show Coming Soon'}
          </TooltipContent>
        )}
      </Tooltip>
    </div>

    {/* Logout */}
    <div className="px-3 py-3 border-t border-slate-800">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className={`w-full flex items-center rounded-md text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors ${
              isCollapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2 text-sm'
            }`}
          >
            <LogOut className="w-[22px] h-[22px]" />
            {!isCollapsed && <span>{t('common.logout')}</span>}
          </button>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700 text-xs">
            {t('common.logout')}
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  </TooltipProvider>
  );
};


// ======================================================================
const NavItem = ({ to, label, icon: Icon, isCollapsed, active, onClick, testId, accent }) => {
  const base = `flex items-center rounded-md transition-colors ${
    isCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2 text-sm font-semibold'
  }`;
  const theme = active
    ? accent
      ? 'bg-gradient-to-r from-[#FF6B35] to-[#F97316] text-white shadow-md shadow-orange-500/30'
      : 'bg-[#00B4D8] text-white'
    : accent
    ? 'text-[#FF6B35] hover:bg-slate-800'
    : 'text-[#F8FAFC] hover:bg-slate-800';

  const content = (
    <Link to={to} onClick={onClick} data-testid={testId} className={`${base} ${theme}`}>
      <Icon className="w-[22px] h-[22px] flex-shrink-0" />
      {!isCollapsed && <span className="flex-1 truncate">{label}</span>}
      {!isCollapsed && accent && (
        <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
          active ? 'bg-white/20 text-white' : 'bg-[#FF6B35]/20 text-[#FF6B35]'
        }`}>Live</span>
      )}
    </Link>
  );

  if (!isCollapsed) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700 text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
};


export default AdminSidebar;
